// context/SelectedSubtitlesContext.jsx
import React, {
  createContext,
  useState,
  useContext,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { useVideo } from "./VideoContext";
import srtParser2 from "srt-parser-2";

const SelectedSubtitlesContext = createContext();

export const useSelectedSubtitles = () => useContext(SelectedSubtitlesContext);

// ========== مدل ساده زیرنویس (بدون نیاز به کلاس) ==========
export const createSubtitleModel = (data) => ({
  // شناسه‌ها
  id:
    data._id ||
    data.id ||
    data.onlineId ||
    `sub-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
  localId:
    data.localId ||
    `local-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
  onlineId: data.onlineId || data._id || data.subtitlesUploadId || null,
  subtitlesUploadId:
    data.subtitlesUploadId || data.onlineId || data._id || null,

  // منبع
  source: data.source || data.sourceType || "unknown",
  sourceType: data.sourceType || data.source || "unknown",

  // اطلاعات پایه
  language: data.language || data.subtitlesLanguage || "unknown",
  subtitlesLanguage: data.subtitlesLanguage || data.language || "unknown",
  fileName: data.fileName || "",

  // اطلاعات فیلم
  nameOfMovie: data.nameOfMovie || data.movieName || "",
  movieName: data.movieName || data.nameOfMovie || "",
  movieFileName: data.movieFileName || "",
  videoType: data.videoType || "movie",
  season: data.season?.toString() || "",
  episode: data.episode?.toString() || "",

  // محتوا
  cues: data.cues || null,
  rawContent: data.rawContent || null,

  // متادیتای آنلاین
  collectionId: data.collectionId || null,
  subOneID: data.subOneID || null,
  subTwoID: data.subTwoID || null,
  langOne: data.langOne || null,
  langTwo: data.langTwo || null,

  // اطلاعات اضافی
  otherTags: data.otherTags || [],
  productionYear: data.productionYear || "",
  coverUrl: data.coverUrl || "",
  description: data.description || "",
  downloadTimes: data.downloadTimes || 0,
  subtitlesScoreValue: data.subtitlesScoreValue || 0,
  subtitlesScoreCount: data.subtitlesScoreCount || 0,
  urlOfMovie: data.urlOfMovie || "",

  // آپلود
  uploadId: data.uploadId || null,
  userId: data.userId || null,
  uploadedFileName: data.uploadedFileName || "",

  // تاریخچه
  addedAt: data.addedAt || new Date().toISOString(),
  lastUsed: data.lastUsed || new Date().toISOString(),
  useCount: data.useCount || 1,

  // پلیر
  targetNumber: data.targetNumber || null,
  isActive: data.isActive || false,
  currentDelay: data.currentDelay || 0,
});

// ========== تابع کمکی مقایسه دو زیرنویس ==========
const isSameSubtitle = (a, b) => {
  if (!a || !b) return false;

  // مقایسه با onlineId (معتبرترین روش)
  if (a.onlineId && b.onlineId && a.onlineId === b.onlineId) return true;

  // مقایسه با subtitlesUploadId
  if (
    a.subtitlesUploadId &&
    b.subtitlesUploadId &&
    a.subtitlesUploadId === b.subtitlesUploadId
  )
    return true;

  // مقایسه با _id
  if (a._id && b._id && a._id === b._id) return true;

  // مقایسه با localId
  if (a.localId && b.localId && a.localId === b.localId) return true;

  // مقایسه با id (آخرین راه)
  if (
    a.id &&
    b.id &&
    a.id === b.id &&
    !a.id.startsWith("sub-") &&
    !b.id.startsWith("sub-")
  ) {
    return true;
  }

  return false;
};

// ========== تشخیص منبع ==========
export const SUBTITLE_SOURCES = {
  ONLINE: "online",
  LOCAL_FILE: "local_file",
  EMBEDDED: "embedded",
  BROWSER_STORAGE: "browser_storage",
  TEMP_MEMORY: "temp_memory",
  SEARCH_RESULT: "search_result",
};

const detectSubtitleSource = (data) => {
  // 🎯 اول sourceType رو بررسی کن (معتبرتر از source)
  if (data.sourceType && Object.values(SUBTITLE_SOURCES).includes(data.sourceType)) {
    return data.sourceType;
  }

  if (data.source && Object.values(SUBTITLE_SOURCES).includes(data.source)) {
    return data.source;
  }

  if (data._id || data.onlineId || data.subtitlesUploadId) {
    return SUBTITLE_SOURCES.ONLINE;
  }

  if (data.source === "embedded" || data.sourceType === "embedded" || data.fileName?.includes("embedded_")) {
    return SUBTITLE_SOURCES.EMBEDDED;
  }

  if (data.id?.startsWith("sub-")) {
    return SUBTITLE_SOURCES.BROWSER_STORAGE;
  }

  if (data.id?.startsWith("local-") || data.source === "local" || data.sourceType === "local_file") {
    return SUBTITLE_SOURCES.LOCAL_FILE;
  }

  return SUBTITLE_SOURCES.TEMP_MEMORY;
};

// ========== Provider ==========
export const SelectedSubtitlesProvider = ({ children }) => {
  const { videoMetadata } = useVideo();

  const [activeSubtitles, setActiveSubtitles] = useState({
    subtitle1: null,
    subtitle2: null,
  });

  const [subtitleHistory, setSubtitleHistory] = useState([]);
  const [isFetchingInfo, setIsFetchingInfo] = useState(false);

  const activeSubtitlesRef = useRef(activeSubtitles);

  useEffect(() => {
    activeSubtitlesRef.current = activeSubtitles;
  }, [activeSubtitles]);

  // دریافت اطلاعات آنلاین
  const fetchOnlineSubtitleInfo = useCallback(async (subtitlesUploadId) => {
    if (!subtitlesUploadId) return null;

    try {
      const response = await fetch(
        "https://umlang.com/subtitles/api/v1/subtitles/findSubtitlesWithId",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ subtitlesUploadId }),
        },
      );

      const result = await response.json();
      if (result.success && result.data?.data?.[0]) {
        return result.data.data[0];
      }
      return null;
    } catch (error) {
      console.error("Error fetching subtitle info:", error);
      return null;
    }
  }, []);

  // استخراج cues از فایل
  const extractCuesFromFile = useCallback(async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsText(file);
      reader.onload = () => {
        try {
          const parser = new srtParser2();
          const cues = parser.fromSrt(reader.result);
          resolve({ cues, rawContent: reader.result });
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
    });
  }, []);

  // تنظیم زیرنویس فعال
  const setActiveSubtitle = useCallback(
    async (subtitleData, targetNumber, file = null) => {
      const key = `subtitle${targetNumber}`;

      if (!subtitleData.source) {
        subtitleData.source = detectSubtitleSource(subtitleData);
      }

      let completeData = { ...subtitleData };

      // دریافت اطلاعات آنلاین اگر نیاز بود
      if (
        (completeData.source === SUBTITLE_SOURCES.ONLINE ||
          completeData.source === SUBTITLE_SOURCES.SEARCH_RESULT) &&
        (completeData.subtitlesUploadId || completeData.onlineId) &&
        !completeData.movieName
      ) {
        const fullInfo = await fetchOnlineSubtitleInfo(
          completeData.subtitlesUploadId || completeData.onlineId,
        );
        if (fullInfo) {
          completeData = {
            ...completeData,
            ...fullInfo,
            onlineId: fullInfo._id,
            source: SUBTITLE_SOURCES.ONLINE,
          };
        }
      }

      // استخراج cues از فایل
      if (file && !completeData.cues) {
        try {
          const extracted = await extractCuesFromFile(file);
          completeData.cues = extracted.cues;
          completeData.rawContent = extracted.rawContent;
        } catch (error) {
          console.error("Error extracting cues from file:", error);
        }
      }

      // پارس rawContent
      if (completeData.rawContent && !completeData.cues) {
        try {
          const parser = new srtParser2();
          completeData.cues = parser.fromSrt(completeData.rawContent);
        } catch (error) {
          console.error("Error parsing rawContent:", error);
        }
      }

      // اضافه کردن متادیتای فیلم
      completeData.movieFileName =
        completeData.movieFileName || videoMetadata.fileName;
      completeData.movieName =
        completeData.movieName ||
        completeData.nameOfMovie ||
        videoMetadata.filmName;
      completeData.nameOfMovie =
        completeData.nameOfMovie ||
        completeData.movieName ||
        videoMetadata.filmName;
      completeData.season = completeData.season || videoMetadata.season;
      completeData.episode = completeData.episode || videoMetadata.episode;
      completeData.videoType =
        completeData.videoType || videoMetadata.videoType;
      completeData.targetNumber = targetNumber;

      const subtitleModel = createSubtitleModel(completeData);

      setActiveSubtitles((prev) => {
        const newState = { ...prev, [key]: subtitleModel };
        activeSubtitlesRef.current = newState;
        return newState;
      });

      // اضافه به تاریخچه
      setSubtitleHistory((prev) => {
        const exists = prev.find((s) => isSameSubtitle(s, subtitleModel));
        if (exists) {
          return prev.map((s) =>
            isSameSubtitle(s, subtitleModel)
              ? {
                  ...s,
                  lastUsed: new Date().toISOString(),
                  useCount: (s.useCount || 0) + 1,
                }
              : s,
          );
        }
        return [subtitleModel, ...prev].slice(0, 50);
      });

      return subtitleModel;
    },
    [videoMetadata, fetchOnlineSubtitleInfo, extractCuesFromFile],
  );

  // پاک کردن زیرنویس فعال
  const clearActiveSubtitle = useCallback((targetNumber) => {
    const key = `subtitle${targetNumber}`;
    setActiveSubtitles((prev) => ({ ...prev, [key]: null }));
  }, []);

  // به‌روزرسانی زبان
  const updateSubtitleLanguage = useCallback((targetNumber, newLanguage) => {
    const key = `subtitle${targetNumber}`;
    setActiveSubtitles((prev) => {
      if (!prev[key]) return prev;
      return { ...prev, [key]: { ...prev[key], language: newLanguage } };
    });
  }, []);

  // به‌روزرسانی تاخیر
  const updateSubtitleDelay = useCallback((targetNumber, newDelay) => {
    const key = `subtitle${targetNumber}`;
    setActiveSubtitles((prev) => {
      if (!prev[key]) return prev;
      return { ...prev, [key]: { ...prev[key], currentDelay: newDelay } };
    });
  }, []);

  // دریافت زیرنویس فعال
  const getActiveSubtitle = useCallback(
    (targetNumber) => {
      return (
        activeSubtitlesRef.current[`subtitle${targetNumber}`] ||
        activeSubtitles[`subtitle${targetNumber}`]
      );
    },
    [activeSubtitles],
  );

  // دریافت cues
  const getActiveSubtitleCues = useCallback(
    (targetNumber) => {
      const subtitle = getActiveSubtitle(targetNumber);
      return subtitle?.cues || null;
    },
    [getActiveSubtitle],
  );

  // 🎯 اضافه کردن به تاریخچه - نسخه نهایی و امن
  const addToHistory = useCallback((subtitleData, targetNumber = null) => {
    // اگر targetNumber داره و data ساده‌ست، مدل بساز
    let model;

    if (subtitleData.id && subtitleData.source) {
      // به احتمال زیاد مدل هست، ولی برای اطمینان نرمالایز کن
      model = createSubtitleModel({
        ...subtitleData,
        targetNumber: targetNumber || subtitleData.targetNumber,
      });
    } else {
      // داده ساده - مدل جدید بساز
      if (!subtitleData.source) {
        subtitleData.source = detectSubtitleSource(subtitleData);
      }
      model = createSubtitleModel({
        ...subtitleData,
        targetNumber,
      });
    }

    setSubtitleHistory((prev) => {
      // 🎯 استفاده از تابع isSameSubtitle (نه متد)
      const exists = prev.find((s) => isSameSubtitle(s, model));
      if (exists) return prev;
      return [model, ...prev].slice(0, 50);
    });
  }, []);

  // دریافت زیرنویس‌های قابل آپلود
  const getUploadableSubtitles = useCallback(() => {
    return subtitleHistory.filter((sub) => {
      // اگر آنلاین هست، نیاز به آپلود نداره
      if (sub.source === "online" || sub.sourceType === "online") return false;
      if (sub.onlineId || sub.subtitlesUploadId) return false;
      return true;
    });
  }, [subtitleHistory]);

  // پاک کردن زیرنویس‌های فعال موقع تغییر فیلم
  useEffect(() => {
    if (videoMetadata.fileName) {
      setActiveSubtitles({ subtitle1: null, subtitle2: null });
    }
  }, [videoMetadata.fileName]);

  const value = {
    activeSubtitles,
    subtitleHistory,

    setActiveSubtitle,
    clearActiveSubtitle,
    updateSubtitleLanguage,
    updateSubtitleDelay,
    getActiveSubtitle,
    getActiveSubtitleCues,
    addToHistory,
    getUploadableSubtitles,

    SUBTITLE_SOURCES,
    isSameSubtitle, // اکسپورت برای استفاده در جاهای دیگه
  };

  return (
    <SelectedSubtitlesContext.Provider value={value}>
      {children}
    </SelectedSubtitlesContext.Provider>
  );
};
